import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { fetchAPI } from '../../../utils/utils';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement } from 'chart.js';
import { applyTimeZone } from '../../../utils/utils';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
    BarElement, 
    CategoryScale, 
    LinearScale, 
    Title, 
    Tooltip, 
    Legend, 
    ChartDataLabels,
    LineElement,
    PointElement
);

function AssistenciesGraph({ eventId, data_inici, data_fi }) {
    const [assistenciesLog, setAssistenciesLog] = useState([]);

    useEffect(() => {
        fetchAPI(`/log_assistencies_event/${eventId}`, (response) => {
            setAssistenciesLog(response.data);
        });
    }, [eventId]);

    const filterOutDuplicates = (logs) => {
        // Create a map to store the latest log for each casteller
        const latestLogPerUser = new Map();

        // For each log, only keep the latest one per user
        logs.forEach(log => {
            const currentDate = new Date(log.updatedAt);
            
            if (!latestLogPerUser.has(log['casteller-id']) || 
                currentDate > new Date(latestLogPerUser.get(log['casteller-id']).updatedAt)) {
                latestLogPerUser.set(log['casteller-id'], log);
            }
        });
        
        // Convert map values back to array and sort by date
        return Array.from(latestLogPerUser.values())
            .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    };

    const processData = (data) => {
        const bins = {};
        const dataWithoutDuplicates = filterOutDuplicates(data);

        dataWithoutDuplicates.forEach(log => {
            const date = new Date(log.updatedAt);
            const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format
            if (!bins[dateString]) {
                bins[dateString] = { 'No vinc': 0, 'Vinc': 0, 'Fitxat': 0, 'No confirmat': 0 };
            }
            const assistencia = parseInt(log.assistència);
            if (assistencia === 0) bins[dateString]['No vinc']++;
            else if (assistencia === 1) bins[dateString]['Vinc']++;
            else if (assistencia === 2) bins[dateString]['Fitxat']++;
            else bins[dateString]['No confirmat']++;
        });

        const labels = Object.keys(bins).sort();
        const noVincData = labels.map(label => bins[label]['No vinc']);
        const vincData = labels.map(label => bins[label]['Vinc']);
        const fitxatData = labels.map(label => bins[label]['Fitxat']);
        const noConfirmatData = labels.map(label => bins[label]['No confirmat']);

        // Calculate cumulative totals
        let cumulative = 0;
        const cumulativeData = labels.map((label) => {
            cumulative += (bins[label]['Vinc']) + (bins[label]['Fitxat']);
            return cumulative;
        });

        return {
            labels,
            datasets: [
                {
                    label: 'No vinc',
                    data: noVincData,
                    backgroundColor: 'red',
                    stack: 'stack1',
                },
                {
                    label: 'Vinc',
                    data: vincData,
                    backgroundColor: 'green',
                    stack: 'stack1',
                },
                {
                    label: 'Assistents',
                    data: cumulativeData,
                    type: 'line',
                    borderColor: 'rgb(75, 192, 192)',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4,
                    yAxisID: 'y1'
                }
            ],
        };
    };

    const chartData = processData(
        assistenciesLog.filter(log => new Date(log.updatedAt) < applyTimeZone(data_inici))
    );

    const options = {
        plugins: {
            title: {
                display: false,
                text: 'Gràfic d\'assistències',
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
            legend: {
                position: 'top',
            },
            datalabels: {
                display: false
            }
        },
        responsive: true,
        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true,
            },
            y1: {
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                title: {
                    display: true,
                }
            }
        },
    };

    return (
        <div style={{ width: '100%', height: 'auto' }}>
            <Bar data={chartData} options={options} />
        </div>
    );
}

export default AssistenciesGraph;
